type GeometricFacetBackgroundProps = {
  idPrefix: string;
  className?: string;
  glowClassName?: string;
  washClassName?: string;
  svgClassName?: string;
  testId?: string;
  tone?: 'workspace' | 'admin' | 'vendor';
  intensity?: 'default' | 'strong';
};

const geometricFacetPolygons = [
  { points: '0,0 400,0 200,300', fillId: 'facet1' },
  { points: '400,0 800,0 600,350', fillId: 'facet2' },
  { points: '800,0 1200,0 1000,280', fillId: 'facet1' },
  { points: '0,0 200,300 0,500', fillId: 'facet3' },
  { points: '200,300 600,350 400,600', fillId: 'facet2' },
  { points: '600,350 1000,280 800,600', fillId: 'facet1' },
  { points: '1000,280 1200,0 1200,500', fillId: 'facet3' },
  { points: '0,500 200,300 400,600', fillId: 'facet1' },
  { points: '400,600 800,600 600,800', fillId: 'facet2' },
  { points: '800,600 1200,500 1200,800', fillId: 'facet3' },
  { points: '0,500 400,600 0,800', fillId: 'facet2' },
  { points: '600,800 1200,800 800,600', fillId: 'facet1' },
  { points: '0,800 600,800 400,600', fillId: 'facet3' },
];

const geometricFacetLines = [
  { x1: '0', y1: '0', x2: '200', y2: '300' },
  { x1: '400', y1: '0', x2: '200', y2: '300' },
  { x1: '400', y1: '0', x2: '600', y2: '350' },
  { x1: '800', y1: '0', x2: '600', y2: '350' },
  { x1: '800', y1: '0', x2: '1000', y2: '280' },
  { x1: '1200', y1: '0', x2: '1000', y2: '280' },
  { x1: '200', y1: '300', x2: '600', y2: '350' },
  { x1: '600', y1: '350', x2: '1000', y2: '280' },
  { x1: '200', y1: '300', x2: '400', y2: '600' },
  { x1: '600', y1: '350', x2: '400', y2: '600' },
  { x1: '600', y1: '350', x2: '800', y2: '600' },
  { x1: '1000', y1: '280', x2: '800', y2: '600' },
  { x1: '1000', y1: '280', x2: '1200', y2: '500' },
  { x1: '0', y1: '500', x2: '200', y2: '300' },
  { x1: '0', y1: '500', x2: '400', y2: '600' },
  { x1: '400', y1: '600', x2: '800', y2: '600' },
  { x1: '800', y1: '600', x2: '1200', y2: '500' },
  { x1: '400', y1: '600', x2: '600', y2: '800' },
  { x1: '800', y1: '600', x2: '600', y2: '800' },
];

export function GeometricFacetBackground({
  idPrefix,
  className,
  glowClassName,
  washClassName,
  svgClassName,
  testId,
  tone = 'workspace',
  intensity = 'default',
}: GeometricFacetBackgroundProps) {
  const toneConfig = {
    workspace: {
      facet1Start: 'hsl(258 90% 66%)',
      facet1End: 'hsl(263 70% 50%)',
      facet2Start: 'hsl(258 90% 72%)',
      facet2End: 'hsl(258 90% 66%)',
      facet3Start: 'hsl(258 90% 66%)',
      facet3End: 'hsl(263 70% 50%)',
      stroke: 'hsl(258 90% 66%)',
    },
    admin: {
      facet1Start: 'hsl(350 90% 64%)',
      facet1End: 'hsl(353 72% 50%)',
      facet2Start: 'hsl(350 90% 72%)',
      facet2End: 'hsl(350 90% 64%)',
      facet3Start: 'hsl(350 90% 64%)',
      facet3End: 'hsl(353 72% 50%)',
      stroke: 'hsl(350 90% 64%)',
    },
    vendor: {
      facet1Start: 'hsl(145 74% 45%)',
      facet1End: 'hsl(160 84% 28%)',
      facet2Start: 'hsl(150 70% 50%)',
      facet2End: 'hsl(145 74% 45%)',
      facet3Start: 'hsl(145 74% 45%)',
      facet3End: 'hsl(160 84% 28%)',
      stroke: 'hsl(145 74% 45%)',
    },
  }[tone];
  const opacityConfig = intensity === 'strong'
    ? {
        facet1Start: '0.18',
        facet1End: '0.1',
        facet2Start: '0.14',
        facet2End: '0.08',
        facet3Start: '0.12',
        facet3End: '0.18',
        stroke: '0.14',
      }
    : {
        facet1Start: '0.08',
        facet1End: '0.04',
        facet2Start: '0.06',
        facet2End: '0.03',
        facet3Start: '0.05',
        facet3End: '0.08',
        stroke: '0.06',
      };

  return (
    <div aria-hidden="true" className={className} data-testid={testId}>
      {glowClassName ? <div className={glowClassName} /> : null}
      {washClassName ? <div className={washClassName} /> : null}
      <svg
        data-testid={testId ? `${testId}-facets` : undefined}
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
        className={svgClassName}
      >
        <defs>
          <linearGradient id={`${idPrefix}-facet1`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={toneConfig.facet1Start} stopOpacity={opacityConfig.facet1Start} />
            <stop offset="100%" stopColor={toneConfig.facet1End} stopOpacity={opacityConfig.facet1End} />
          </linearGradient>
          <linearGradient id={`${idPrefix}-facet2`} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={toneConfig.facet2Start} stopOpacity={opacityConfig.facet2Start} />
            <stop offset="100%" stopColor={toneConfig.facet2End} stopOpacity={opacityConfig.facet2End} />
          </linearGradient>
          <linearGradient id={`${idPrefix}-facet3`} x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor={toneConfig.facet3Start} stopOpacity={opacityConfig.facet3Start} />
            <stop offset="100%" stopColor={toneConfig.facet3End} stopOpacity={opacityConfig.facet3End} />
          </linearGradient>
        </defs>
        {geometricFacetPolygons.map((facet) => (
          <polygon
            key={`${idPrefix}-${facet.points}`}
            points={facet.points}
            fill={`url(#${idPrefix}-${facet.fillId})`}
          />
        ))}
        <g stroke={toneConfig.stroke} strokeWidth="0.45" opacity={opacityConfig.stroke} fill="none">
          {geometricFacetLines.map((line) => (
            <line key={`${idPrefix}-${line.x1}-${line.y1}-${line.x2}-${line.y2}`} {...line} />
          ))}
        </g>
      </svg>
    </div>
  );
}
